import React, { useCallback, useMemo } from 'react';
import { pick, pipe } from 'ramda';
import { useLocation } from 'react-router-dom';
import { Link } from 'js/components/common/Link';
import { useAppSelector } from 'js/store/hooks';
import { BackboneReplacer } from 'js/backbone/components/BackboneReplacer';
import { useOpenChat, useOpenInbox } from 'js/components/system/CommunicationWidgets/hooks';

import * as hooks from './hooks';
import * as helpers from './helpers';

import style from './NavigationBar.scss';

export const NavigationBar = ({ currentPageURL }) => {
    const location = useLocation();
    const { rootPage, menuItems, chatWidget, inboxWidget } = useAppSelector(pick(['rootPage', 'menuItems', 'chatWidget', 'inboxWidget']));
    const isActive = item => item.url === currentPageURL || currentPageURL === '/' && item.url === rootPage;
    const filterByRoleAvailability = hooks.useFilterByRoleAvailability();
    const openInbox = useOpenInbox();
    const openChat = useOpenChat();
    const onOpenInbox = useCallback((event) => {
        event.preventDefault();
        openInbox();
    }, [openInbox]);
    const onOpenChat = useCallback((event) => {
        event.preventDefault();
        openChat();
    }, [openChat]);

    const currentMenuItems = useMemo(() => menuItems?.length ? pipe(
        helpers.getMenuSiblingsByUrl(currentPageURL || rootPage),
        filterByRoleAvailability,
        helpers.markMenuItemAsOpener(chatWidget, inboxWidget),
        helpers.filterByUrlAvailability(location.search)
    )(menuItems) : [], [
        location,
        rootPage,
        menuItems,
        chatWidget,
        inboxWidget,
        currentPageURL,
        filterByRoleAvailability
    ]);

    if (!currentMenuItems.length) {
        return null;
    }

    return (
        <div className={`${style.container} cms-navigation-bar`}>
            <ul className={style.list}>
                {currentMenuItems.map(item => (
                    <div key={item.friendlyURL}>
                        {item.inboxOpener && (
                            <li className={`${style.listItem} ${item.cssClasses ? `${item.cssClasses} ` : ''}`}>
                                <a
                                   href='#'
                                   className={`${style.listItemPoint} ${style.listItemPointReplacer}`}
                                   onClick={onOpenInbox}>
                                    <div className={style.replacerContainer}>
                                        <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                        <span className={`${style.itemName} ${style.replacerItem}`}>{item.name}</span>
                                        <BackboneReplacer dynamicName={'UNREAD_MESSAGES_COUNT'} viewName={'unreadmessagescount'} />
                                    </div>
                                </a>
                            </li>
                        )}
                        {item.chatOpener && (
                            <li className={`${style.listItem} ${item.cssClasses ? `${item.cssClasses} ` : ''}`}>
                                <a
                                   href='#'
                                   className={`${style.listItemPoint} ${style.listItemPointReplacer}`}
                                   onClick={onOpenChat}>
                                    <div className={style.replacerContainer}>
                                        <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                        <span className={`${style.itemName} ${style.replacerItem}`}>{item.name}</span>
                                        <BackboneReplacer dynamicName={'CHAT_UNREAD'} viewName={'chatunread'} />
                                    </div>
                                </a>
                            </li>
                        )}
                        {!item.inboxOpener && !item.chatOpener && (
                            <li
                                className={
                                    `${style.listItem} ${item.cssClasses ? `${item.cssClasses} ` : ''}${isActive(item) ? `${style.isActive} active` : ''}`
                                }
                                data-url={item.url}
                            >
                                <Link
                                    href={item.url}
                                    inNewWindow={item.target === '_blank'}
                                    className={`${style.link} ${style.listItemPoint}`}
                                >
                                    <span className={`micon-${item.menuIcon ? item.menuIcon : ''}`} />
                                    <span className={style.itemName}>{item.name}</span>
                                </Link>
                            </li>
                        )}
                    </div>
                ))}
            </ul>
        </div>
    );
};
